import { DateTime } from "../utils/dateTime";
import { BaseRecord } from "./BaseRecord";
import { BaseRootRepository } from "./repository/BaseRootRepository";
import { RecordRepository } from "./repository/RecordRepository";
import { TournamentRecord } from "./TournamentRecord";

export class TournamentStageRecord<TJsonData extends TournamentStageRecord.CtorJsonData = TournamentStageRecord.CtorJsonData> extends BaseRecord<TJsonData> {
  name?: string
  order_number?: number
  data_approved_at?: Date | null
  data_frozen_at?: Date | null

  /// >>> tournament >>>
  #tournament:BaseRecord.RefField<TournamentRecord> = {}
  
  tournament_id?: string

  get tournament() {
    return this.#tournament.object
  }

  getTournament() {
    this.#tournament.id = this.tournament_id

    return this.getObjectFromRefField(this.#tournament, "tournaments")
  }
  /// <<< tournament <<<

  /// >>> computed_values >>>
  get isApproved() {
    return !!this.data_approved_at
  }

  get isFrozen() {
    return !!this.data_frozen_at
  }
  
  get sortValues() {
    return [
      this.order_number ?? Number.MAX_SAFE_INTEGER,
    ]
  }
  get tournament_name() {
    return this.tournament?.name
  }
  /// <<< computed_values <<<

  static createNew(db?:BaseRootRepository.OrNothing, jsonData?:TournamentStageRecord.CtorJsonData) {
    return new TournamentStageRecord(db, jsonData)
  }

  constructor(db?:BaseRootRepository.OrNothing, jsonData?:TJsonData) {
    super(db)

    if (jsonData) {
      this.patchData(jsonData)
    }
  }

  patchData(jsonData:TJsonData) {
    super.patchData(jsonData)

    this.tournament_id = jsonData?.tournament_id
    this.name = jsonData?.name
    this.order_number = jsonData?.order_number
    this.data_approved_at = DateTime.parseDateOrNull(jsonData?.data_approved_at)
    this.data_frozen_at = DateTime.parseDateOrNull(jsonData?.data_frozen_at)
  }

  toJson():TJsonData {
    return Object.assign(super.toJson(), {
      tournament_id: this.tournament_id,
      name: this.name,
      order_number: this.order_number,
      data_approved_at: this.data_approved_at?.toISOString(),
      data_frozen_at: this.data_frozen_at?.toISOString()
    }) as TJsonData
  }

  // #alreadyPreloaded = false

  async preload() {
    // if(this.#alreadyPreloaded) return

    await super.preload()

    // this.#alreadyPreloaded = true

    const tournament = await this.getTournament()
    await tournament?.preload()
  }
}

export module TournamentStageRecord {
  export type CtorJsonData = BaseRecord.CtorJsonData & {
    tournament_id?: string
    name?: string
    order_number?: number
    data_approved_at?: string
    data_frozen_at?: string
  }

  export class Repository extends RecordRepository<TournamentStageRecord> {
    #getAll:RecordRepository.GetFieldFlags = {}
    getAll(options?:RecordRepository.GetOptions) {
      return this.peekOrLoad(this.#getAll, options, {
        peek: () => this.peekAll(),
        load: () => this.loadAll()
      })
    }

    /// >>> by tournament_id >>>
    #getByTournamentId:RecordRepository.NestingGetFieldFlags = {}

    getByTournamentId(tournamentId:string, options?:RecordRepository.GetOptions) {
      return this.nestedPeekOrLoad(this.#getByTournamentId, tournamentId, options, {
        peek: () => this.peekByTournamentId(tournamentId),
        load: () => this.loadByTournamentId(tournamentId)
      })
    }

    peekByTournamentId(tournamentId:string) {
      return this.peekAll(x => x?.tournament_id == tournamentId)
    }

    create(record: Partial<TournamentStageRecord<CtorJsonData>>): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.createOneTournamentStage(record)
    }

    protected loadAll() {
      return this.getApi().getAllTournamentStages()
    }

    protected loadByTournamentId(tournamentId:string) {
      return this.getApi().getMoreTournamentStagesByTournamentId(tournamentId)
    }
    /// <<< by tournament id <<<

    protected loadById(id: string) {
      return this.getApi().getOneTournamentStageById(id)
    }

    protected updateById(id: string, patch: Partial<TournamentStageRecord<CtorJsonData>>): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.updateOneTournamentStage(id, patch)
    }

    protected deleteById(id: string): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.deleteOneTournamentStage(id)
    }

    protected approveById(id: string, patch: Partial<TournamentStageRecord<CtorJsonData>>): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.approveOneTournamentStageById(id, patch)
    }

    protected unapproveById(id: string, patch: Partial<TournamentStageRecord<CtorJsonData>>): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.unapproveOneTournamentStageById(id, patch)
    }

    protected freezeById(id: string, patch: Partial<TournamentStageRecord<CtorJsonData>>): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.freezeOneTournamentStageById(id, patch)
    }

    protected unfreezeById(id: string, patch: Partial<TournamentStageRecord<CtorJsonData>>): Promise<TournamentStageRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.unfreezeOneTournamentStageById(id, patch)
    }

    protected touchAll_(): Promise<number | null> {
      const api = this.getApiOrThrow()
      return api.touchAllTournamentStages()
    }

    protected touchById(id: string): Promise<number | null> {
      const api = this.getApiOrThrow()
      return api.touchOneTournamentStageById(id)
    }
  }
}